// Data-dictionary findings API client. Mirrors the conventions of
// lib/overridesApi.ts so the surface stays consistent across the
// resolver-related panels.

import type {
  ApplyRetentionFixRequest,
  DataDictionaryFindingsResponse,
} from '@/types/dataDictionaryFindings'

const API_BASE = '/api'

function getOrCreateClientId(): string {
  const KEY = 'ai_alm.client_id'
  try {
    let id = window.localStorage.getItem(KEY)
    if (!id) {
      id = `c_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`
      window.localStorage.setItem(KEY, id)
    }
    return id
  } catch {
    return 'c_anonymous'
  }
}

function newRequestId(): string {
  return `r_${Math.random().toString(36).slice(2, 10)}`
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Id': getOrCreateClientId(),
      'X-Request-Id': newRequestId(),
      ...(init?.headers ?? {}),
    },
  })
  if (!response.ok) {
    let detail = ''
    try {
      const envelope = await response.json()
      detail =
        envelope?.error?.message ??
        envelope?.detail ??
        response.statusText ??
        `HTTP ${response.status}`
    } catch {
      detail = response.statusText || `HTTP ${response.status}`
    }
    throw new Error(detail)
  }
  return (await response.json()) as T
}

export function getDataDictionaryFindings(
  projectId: string,
  runId: number,
): Promise<DataDictionaryFindingsResponse> {
  return jsonFetch(
    `/projects/${projectId}/runs/${runId}/data-dictionary-findings`,
  )
}

/**
 * POST .../data-dictionary-findings/apply-retention-fix
 *
 * Set a table's retention_policy to a concrete value (Tier-2). The
 * response is a fresh findings snapshot — the retention finding
 * clears if the new value satisfies the resolver.
 */
export function applyRetentionFix(
  projectId: string,
  runId: number,
  body: ApplyRetentionFixRequest,
): Promise<DataDictionaryFindingsResponse> {
  return jsonFetch(
    `/projects/${projectId}/runs/${runId}/data-dictionary-findings/apply-retention-fix`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}
